import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { fallanalyseAPI, sammelfallAPI } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { LagebeurteilungView } from '@/components/fallanalyse/LagebeurteilungView';
import { SzenarioView } from '@/components/fallanalyse/SzenarioView';
import { AnschlussoptionenView } from '@/components/fallanalyse/AnschlussoptionenView';
import { VertrauenshinweiseView } from '@/components/fallanalyse/VertrauenshinweiseView';
import { EntwurfBearbeiten } from '@/components/fallanalyse/EntwurfBearbeiten';
import {
  trackDashboardEntwurfGeoeffnet,
  trackDashboardNeuerCheck,
  trackPremiumInteresseRegistriert,
} from '@/lib/tracking';
import {
  Plus,
  ArrowRight,
  Compass,
  ShieldAlert,
  HelpCircle,
  FileText,
  Clock,
  Sparkles,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Pencil,
  Loader2,
  RefreshCw,
  Lock,
  Download,
  CreditCard,
  ExternalLink,
  FlaskConical,
  RotateCcw,
  Users,
  FolderOpen,
  Megaphone,
  Send,
  Mail,
  MessageCircle,
} from 'lucide-react';

// ============================================
// ENTWURF-KARTE (mit Bearbeitung + Lagebeurteilung)
// ============================================
function EntwurfCard({ entwurf, onPremiumInteresse, onRefresh, t, istTestBerechtigt, sprache }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbData, setLbData] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const vorentscheidung = entwurf.vorentscheidung;
  const aufgabenOffen = (entwurf.aufgaben || []).filter(a => a.status === 'offen').length;
  const aufgabenGesamt = (entwurf.aufgaben || []).length;
  const hatLB = entwurf.lagebeurteilung_status === 'fertig' && (entwurf.lagebeurteilung || entwurf.lagebeurteilung_typ);
  const lbVerarbeitung = entwurf.lagebeurteilung_status === 'verarbeitung';
  const bundesland = entwurf.bundesland || entwurf.erkannte_daten?.Standort || entwurf.erkannte_daten?.Bundesland || '';

  // Polling für Lagebeurteilung
  const startPolling = useCallback(() => {
    setLbLoading(true);
    const poll = async () => {
      try {
        const res = await fallanalyseAPI.lagebeurteilungStatus(entwurf.entwurf_id);
        const d = res.data;
        if (d.status === 'fertig') {
          setLbData(d);
          setLbLoading(false);
          clearInterval(pollRef.current);
          toast.success(t('dashboard.toastLbFertig'));
          onRefresh?.();
        } else if (d.status === 'fehler') {
          setLbLoading(false);
          clearInterval(pollRef.current);
          toast.error(d.hinweis || t('dashboard.toastLbFehler'));
          onRefresh?.();
        }
      } catch {
        /* weiter pollen */
      }
    };
    poll();
    pollRef.current = setInterval(poll, 4000);
  }, [entwurf.entwurf_id, onRefresh, t]);

  useEffect(() => {
    if (lbVerarbeitung && expanded) {
      startPolling();
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [lbVerarbeitung, expanded, startPolling]);

  const handleExpand = () => {
    if (!expanded) trackDashboardEntwurfGeoeffnet(entwurf.entwurf_id);
    setExpanded(!expanded);
  };

  const handleSaveEdit = async (data) => {
    setSaving(true);
    try {
      await fallanalyseAPI.entwurfAktualisieren(entwurf.entwurf_id, data);
      toast.success(t('dashboard.toastGespeichert'));
      setEditing(false);
      onRefresh?.();
    } catch {
      toast.error(t('dashboard.toastSpeichernFehler'));
    } finally {
      setSaving(false);
    }
  };

  const handleLagebeurteilung = async () => {
    const bl = entwurf.bundesland || entwurf.erkannte_daten?.Standort || entwurf.erkannte_daten?.Bundesland;
    if (!bl) {
      toast.error(t('dashboard.toastBundeslandFehlt'));
      setEditing(true);
      return;
    }
    try {
      const res = await fallanalyseAPI.lagebeurteilungAnfordern(entwurf.entwurf_id, bl);
      if (res.data.status === 'bundesland_fehlt') {
        toast.error(res.data.hinweis);
        setEditing(true);
        return;
      }
      toast.info(t('dashboard.toastLbWird'));
      startPolling();
    } catch {
      toast.error(t('dashboard.toastLbStartFehler'));
    }
  };

  const handleVorschauPdf = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${apiBase}/api/cases/entwurf/${entwurf.entwurf_id}/vorschau-pdf`, '_blank');
      toast.success(t('dashboard.toastKurzfassung'));
    } catch {
      toast.error(t('dashboard.toastKurzfassungFehler'));
    }
  };

  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleStufeCFreischalten = async () => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await fallanalyseAPI.stufeCCheckout(entwurf.entwurf_id, origin);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setCheckoutLoading(false);
      const backendMsg = err?.response?.data?.detail;
      toast.error(backendMsg || t('dashboard.toastZahlungFehler'));
    }
  };

  const handleStufeCPdfDownload = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${apiBase}/api/cases/entwurf/${entwurf.entwurf_id}/pdf`, '_blank');
      toast.success(t('dashboard.toastFkDownload'));
    } catch (err) {
      const backendMsg = err?.response?.data?.detail;
      toast.error(backendMsg || t('dashboard.toastFkFehler'));
    }
  };

  const handleSzenarioPdfDownload = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${apiBase}/api/cases/entwurf/${entwurf.entwurf_id}/szenario-pdf`, '_blank');
      toast.success(t('dashboard.toastSzenarioDownload'));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.info(t('dashboard.toastSzenarioHinweis'));
        return;
      }
      const backendMsg = err?.response?.data?.detail;
      toast.error(backendMsg || t('dashboard.toastSzenarioFehler'));
    }
  };

  const handleTestUnlock = async () => {
    setTestLoading(true);
    try {
      await fallanalyseAPI.testUnlock(entwurf.entwurf_id);
      toast.success(t('dashboard.testmodusErfolg'));
      onRefresh?.();
    } catch (err) {
      const backendMsg = err?.response?.data?.detail;
      toast.error(backendMsg || t('dashboard.toastTestFehler'));
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestReset = async () => {
    setTestLoading(true);
    try {
      await fallanalyseAPI.testReset(entwurf.entwurf_id);
      toast.success(t('dashboard.testmodusResetErfolg'));
      onRefresh?.();
    } catch (err) {
      const backendMsg = err?.response?.data?.detail;
      toast.error(backendMsg || t('dashboard.toastTestResetFehler'));
    } finally {
      setTestLoading(false);
    }
  };

  const erstelltDatum = entwurf.erstellt
    ? new Date(entwurf.erstellt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  const displayLB = lbData?.lagebeurteilung || entwurf.lagebeurteilung;
  const displayLBTyp = lbData?.typ || entwurf.lagebeurteilung_typ;

  // 5-Schritt Fortschritt berechnen
  const fortschritt = {
    fallBeschrieben: true, // Immer true wenn Entwurf existiert
    pflichtangaben: !!bundesland,
    lbAngefordert: !!entwurf.lagebeurteilung_status,
    lbFertig: hatLB,
    stufeCFreigeschaltet: entwurf.stufe_c_status === 'freigeschaltet',
  };
  const fortschrittSchritte = [fortschritt.fallBeschrieben, fortschritt.pflichtangaben, fortschritt.lbAngefordert || fortschritt.lbFertig, fortschritt.lbFertig, fortschritt.stufeCFreigeschaltet];
  const aktuellerSchritt = fortschrittSchritte.filter(Boolean).length;

  // "Jetzt tun" / "Danach passiert" Logik
  const getJetztTunText = () => {
    if (fortschritt.stufeCFreigeschaltet) return t('dashboard.jetztFertig');
    if (fortschritt.lbFertig) return t('dashboard.jetztStufeCFreischalten');
    if (lbLoading || lbVerarbeitung) return t('dashboard.jetztLbWarten');
    if (fortschritt.pflichtangaben) return t('dashboard.jetztLbAnfordern');
    return t('dashboard.jetztBundesland');
  };
  const getDanachText = () => {
    if (fortschritt.stufeCFreigeschaltet) return null;
    if (fortschritt.lbFertig) return t('dashboard.danachPdf');
    if (fortschritt.pflichtangaben) return t('dashboard.danachStufeC');
    return t('dashboard.danachLb');
  };

  const [detailsOffen, setDetailsOffen] = useState(false);

  return (
    <Card className="border-warm-200 hover:border-primary/20 transition-colors" data-testid={`entwurf-card-${entwurf.entwurf_id}`}>
      <CardContent className="p-0">
        {/* Header */}
        <button onClick={handleExpand} className="w-full text-left p-5 pb-4" data-testid={`entwurf-toggle-${entwurf.entwurf_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <Badge variant="outline" className="text-[11px] border-warm-300 text-muted-foreground">
                  {entwurf.zielgruppe || 'Waldbesitzer'}
                </Badge>
                {bundesland && (
                  <Badge variant="outline" className="text-[11px] border-primary/20 text-primary/70">
                    {bundesland}
                  </Badge>
                )}
                {hatLB && (
                  <Badge className="bg-primary/10 text-primary text-[11px] border-0" data-testid="lb-badge">
                    {t('produkt.lagebeurteilung')}
                  </Badge>
                )}
                {entwurf.premium_interesse && (
                  <Badge className="bg-primary/10 text-primary text-[11px] border-0" data-testid="premium-interesse-badge">
                    {t('dashboard.interesseVorgemerkt')}
                  </Badge>
                )}
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {erstelltDatum}
                </span>
              </div>
              <p className="text-sm font-medium text-foreground leading-relaxed line-clamp-2">
                {entwurf.zusammenfassung || entwurf.freitext?.substring(0, 120) + '...'}
              </p>
              {entwurf.empfohlener_weg && (
                <p className="text-xs text-primary/70 mt-1 font-medium">Empfehlung: {entwurf.empfohlener_weg}</p>
              )}
            </div>
            <div className="shrink-0 mt-1">
              {expanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
        </button>

        {/* Expanded Detail */}
        {expanded && (
          <div className="px-5 pb-5 pt-0 border-t border-warm-100" data-testid={`entwurf-detail-${entwurf.entwurf_id}`}>
            {/* Testmodus-Banner */}
            {entwurf.unlock_source === 'internal_test' && (
              <div className="mt-4 flex items-center gap-2.5 p-3 rounded-lg border-2 border-amber-400 bg-amber-50" data-testid={`testmodus-banner-${entwurf.entwurf_id}`}>
                <FlaskConical className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">{t('dashboard.testmodusBanner')}</p>
                  {entwurf.unlock_by && (
                    <p className="text-[11px] text-amber-600 mt-0.5">
                      Freigeschaltet von: {entwurf.unlock_by}
                      {entwurf.unlock_at && ` | ${new Date(entwurf.unlock_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                    </p>
                  )}
                </div>
                {istTestBerechtigt && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestReset}
                    disabled={testLoading}
                    className="shrink-0 h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                    data-testid={`cta-test-reset-${entwurf.entwurf_id}`}
                  >
                    {testLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                    {t('dashboard.testmodusReset')}
                  </Button>
                )}
              </div>
            )}

            {/* 5-Schritt-Fortschrittsanzeige */}
            {!editing && (
              <div className="mt-4 mb-4" data-testid={`fortschritt-${entwurf.entwurf_id}`}>
                <div className="flex items-center gap-1">
                  {[
                    { done: fortschritt.fallBeschrieben, label: t('dashboard.fortFallBeschrieben') },
                    { done: fortschritt.pflichtangaben, label: t('dashboard.fortPflichtangaben') },
                    { done: fortschritt.lbFertig || fortschritt.lbAngefordert, label: t('dashboard.fortLbAnfordern') },
                    { done: fortschritt.lbFertig, label: t('dashboard.fortLbFertig') },
                    { done: fortschritt.stufeCFreigeschaltet, label: t('dashboard.fortStufeCFreischalten') },
                  ].map((s, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-full h-1.5 rounded-full transition-colors ${
                        s.done ? 'bg-primary' : 'bg-gray-200'
                      }`} />
                      <span className={`text-[10px] leading-tight text-center ${
                        s.done ? 'text-primary font-medium' : 'text-muted-foreground/60'
                      }`}>{s.label}</span>
                    </div>
                  ))}
                </div>

                {/* Jetzt-tun / Danach-Box */}
                <div className="mt-3 p-3 rounded-lg border border-primary/10 bg-primary/[0.02]" data-testid={`jetzt-tun-${entwurf.entwurf_id}`}>
                  <div className="flex items-start gap-2.5">
                    <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-0.5">{t('dashboard.jetztTun')}</p>
                      <p className="text-sm text-foreground leading-relaxed">{getJetztTunText()}</p>
                      {getDanachText() && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                          <span className="font-medium">{t('dashboard.danachPassiert')}:</span> {getDanachText()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bearbeitungsmodus */}
            {editing ? (
              <div className="mt-4">
                <EntwurfBearbeiten
                  entwurf={entwurf}
                  onSave={handleSaveEdit}
                  onCancel={() => setEditing(false)}
                  loading={saving}
                  t={t}
                />
              </div>
            ) : (
              <>
                {/* Einklappbare Details: Vorentscheidung, Offene Punkte, Ampeln */}
                {(vorentscheidung || aufgabenGesamt > 0 || entwurf.ampeln?.length > 0) && (
                  <div className="mt-2">
                    <button
                      onClick={() => setDetailsOffen(!detailsOffen)}
                      className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      data-testid={`details-toggle-${entwurf.entwurf_id}`}
                    >
                      {detailsOffen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {detailsOffen ? t('dashboard.detailsAusblenden') : t('dashboard.detailsEinblenden')}
                      {!detailsOffen && aufgabenOffen > 0 && (
                        <span className="text-[10px] text-amber-600 ml-1">({aufgabenOffen} offen)</span>
                      )}
                    </button>

                    {detailsOffen && (
                      <div className="mt-3 space-y-3 animate-in fade-in-0 slide-in-from-top-1 duration-200">
                        {/* Vorentscheidung */}
                        {vorentscheidung && (
                          <div className="space-y-2">
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('dashboard.vorentscheidung')}</h3>
                            <div className="flex items-start gap-2.5 p-3 rounded-lg border border-primary/10 bg-primary/[0.02]">
                              <Compass className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-primary/70 uppercase tracking-wide">{t('dashboard.sinnvollsterWeg')}</p>
                                <p className="text-sm text-foreground leading-relaxed">{vorentscheidung.sinnvollster_weg}</p>
                              </div>
                            </div>
                            {vorentscheidung.groesstes_risiko && (
                              <div className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200/60 bg-amber-50/30">
                                <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">{t('dashboard.risiko')}</p>
                                  <p className="text-sm text-foreground leading-relaxed">{vorentscheidung.groesstes_risiko.text}</p>
                                </div>
                              </div>
                            )}
                            {vorentscheidung.wichtigster_offener_punkt && (
                              <div className="flex items-start gap-2.5 p-3 rounded-lg border border-warm-200 bg-warm-50/30">
                                <HelpCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('dashboard.offenerPunkt')}</p>
                                  <p className="text-sm text-foreground">{vorentscheidung.wichtigster_offener_punkt.beschreibung}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Offene Aufgaben */}
                        {aufgabenGesamt > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                              {t('dashboard.offenePunkte')} ({aufgabenOffen} von {aufgabenGesamt})
                            </h3>
                            <div className="space-y-1.5">
                              {entwurf.aufgaben.slice(0, 4).map((a, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                  {a.status === 'offen' ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> : <CheckCircle2 className="h-3.5 w-3.5 text-primary/50 mt-0.5 shrink-0" />}
                                  <span className="leading-relaxed">{a.label || a.frage}</span>
                                </div>
                              ))}
                              {entwurf.aufgaben.length > 4 && <p className="text-xs text-muted-foreground pl-5">+ {entwurf.aufgaben.length - 4} weitere</p>}
                            </div>
                          </div>
                        )}

                        {/* Ampeln */}
                        {entwurf.ampeln?.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('dashboard.einschaetzung')}</h3>
                            <div className="flex flex-wrap gap-2">
                              {entwurf.ampeln.map((a, i) => {
                                const colors = { gruen: 'bg-emerald-50 text-emerald-700 border-emerald-200', gelb: 'bg-amber-50 text-amber-700 border-amber-200', rot: 'bg-red-50 text-red-700 border-red-200' };
                                return (
                                  <span key={i} className={`inline-flex items-center text-[11px] font-medium px-2 py-1 rounded-md border ${colors[a.farbe] || colors.gelb}`}>{a.titel}</span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* KI-Lagebeurteilung */}
                {displayLB && displayLBTyp === 'ki_lagebeurteilung' && (
                  <div className="mt-5 pt-4 border-t border-primary/10">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      KI-Lagebeurteilung
                    </h3>
                    <LagebeurteilungView
                      lb={displayLB}
                      t={t}
                    />
                  </div>
                )}

                {/* Regelbasierte Einordnung (Fallback – klar reduziert) */}
                {(entwurf.lagebeurteilung_typ === 'regelbasierte_einordnung' || (lbData && lbData.typ === 'regelbasierte_einordnung')) && (
                  <div className="mt-4 p-4 rounded-lg border border-amber-200 bg-amber-50/30" data-testid="regelbasiert-hinweis">
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
                          Regelbasierte Einordnung (reduziert)
                        </p>
                        <p className="text-sm text-amber-800 leading-relaxed">
                          {entwurf.lagebeurteilung_hinweis || lbData?.hinweis || t('dashboard.toastLbFehler')}
                        </p>
                        <p className="text-xs text-amber-600 mt-1.5">
                          Dies ist keine vollständige KI-Lagebeurteilung. Sie sehen eine eingeschränkte Einordnung auf Basis der vorhandenen Regellogik.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status-Anzeige: Verarbeitung */}
                {(lbLoading || lbVerarbeitung) && !displayLB && (
                  <div className="mt-4 p-4 rounded-lg border border-primary/10 bg-primary/[0.02] flex items-center gap-3" data-testid="lb-loading">
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('dashboard.lagebeurteilungWird')}</p>
                      <p className="text-xs text-muted-foreground">{t('dashboard.lagebeurteilungDauer')}</p>
                    </div>
                  </div>
                )}

                {/* Status-Anzeige: Fehler */}
                {entwurf.lagebeurteilung_status === 'fehler' && entwurf.lagebeurteilung_typ !== 'regelbasierte_einordnung' && (
                  <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50/30 flex items-start gap-2.5" data-testid="lb-fehler">
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">{t('dashboard.lagebeurteilungFehler')}</p>
                      <p className="text-xs text-red-600 mt-0.5">{entwurf.lagebeurteilung_hinweis || t('dashboard.toastLbFehler')}</p>
                    </div>
                  </div>
                )}

                {/* Zeitstempel der letzten Lagebeurteilung */}
                {hatLB && entwurf.lagebeurteilung_erstellt && (
                  <p className="mt-3 text-[11px] text-muted-foreground/60" data-testid="lb-zeitstempel">
                    Lagebeurteilung erstellt: {new Date(entwurf.lagebeurteilung_erstellt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {entwurf.lagebeurteilung_bundesland && ` | ${entwurf.lagebeurteilung_bundesland}`}
                  </p>
                )}

                {/* Szenario: "Was passiert wenn?" */}
                {hatLB && (
                  <SzenarioView
                    entwurfId={entwurf.entwurf_id}
                    stufeCStatus={entwurf.stufe_c_status}
                    t={t}
                    sprache={sprache}
                  />
                )}

                {/* Stufe D: Anschlussoptionen "Wie geht es weiter?" */}
                {hatLB && (
                  <AnschlussoptionenView
                    entwurfId={entwurf.entwurf_id}
                    stufeCStatus={entwurf.stufe_c_status}
                    lbTyp={displayLBTyp}
                    t={t}
                  />
                )}

                {/* Vertrauenshinweise: Grundlagen, Anlaufstellen, Abgrenzung */}
                {hatLB && (
                  <VertrauenshinweiseView
                    entwurfId={entwurf.entwurf_id}
                    lbTyp={displayLBTyp}
                    t={t}
                  />
                )}
              </>
            )}

            {/* CTAs */}
            {!editing && (
              <div className="mt-5 space-y-2">
                {/* Bundesland-Pflichthinweis wenn nicht gesetzt */}
                {!bundesland && (
                  <div className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50/30" data-testid="bundesland-pflicht-hinweis">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-amber-800">{t('dashboard.bundeslandPflicht')}</p>
                      <Button
                        variant="link"
                        onClick={() => setEditing(true)}
                        className="h-auto p-0 text-sm text-amber-700 font-medium mt-1"
                        data-testid="cta-bundesland-setzen"
                      >
                        {t('dashboard.bundeslandSetzen')}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Lagebeurteilung anfordern / neu berechnen – nur mit Bundesland */}
                  {!lbLoading && !lbVerarbeitung && bundesland && (
                    <Button
                      onClick={handleLagebeurteilung}
                      className="flex-1 h-10 text-sm"
                      data-testid={`cta-lagebeurteilung-${entwurf.entwurf_id}`}
                    >
                      {hatLB || displayLB ? (
                        <><RefreshCw className="h-4 w-4 mr-2" />{t('dashboard.lagebeurteilungNeu')}</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" />{t('dashboard.lagebeurteilungAnfordern')}</>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    className="h-10 text-sm border-warm-300"
                    data-testid={`cta-bearbeiten-${entwurf.entwurf_id}`}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    {t('dashboard.angabenBearbeiten')}
                  </Button>
                </div>

                {/* PDF-Bereich: Kurzfassung + Fallklärung */}
                {hatLB && (
                  <div className="space-y-2" data-testid={`pdf-bereich-${entwurf.entwurf_id}`}>
                    {/* Stufe B: Kurzfassung (frei verfügbar) */}
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-primary/15 bg-primary/[0.02]">
                      <Download className="h-4 w-4 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{t('dashboard.vorschauPdf')}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                          {t('dashboard.vorschauPdfHinweis')}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleVorschauPdf}
                        className="shrink-0 h-8 text-xs border-primary/30 text-primary hover:bg-primary/5"
                        data-testid={`cta-vorschau-pdf-${entwurf.entwurf_id}`}
                      >
                        <Download className="h-3 w-3 mr-1.5" />
                        PDF
                      </Button>
                    </div>

                    {/* Stufe C: Nur bei ki_lagebeurteilung verfügbar */}
                    {displayLBTyp === 'ki_lagebeurteilung' ? (
                      <>
                        {entwurf.stufe_c_status === 'freigeschaltet' ? (
                          /* FREIGESCHALTET: Download-Buttons */
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 p-3 rounded-lg border border-green-200 bg-green-50/40" data-testid={`pdf-freigeschaltet-${entwurf.entwurf_id}`}>
                              <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground">{t('dashboard.vollstaendigeVorlage')}</p>
                                <p className="text-xs text-green-700 leading-relaxed mt-0.5">{t('dashboard.stufeCFreigeschaltet')}</p>
                              </div>
                              <Button
                                size="sm"
                                onClick={handleStufeCPdfDownload}
                                className="shrink-0 h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`cta-stufec-download-${entwurf.entwurf_id}`}
                              >
                                <Download className="h-3 w-3 mr-1.5" />
                                PDF
                              </Button>
                            </div>
                            {/* Szenario-Anhang: Nur sichtbar wenn Szenarien vorhanden */}
                            {entwurf.szenarien && Object.keys(entwurf.szenarien).length > 0 && (
                              <div className="flex items-center gap-2 p-2.5 rounded-lg border border-blue-200 bg-blue-50/30" data-testid={`szenario-pdf-${entwurf.entwurf_id}`}>
                                <FileText className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-foreground">Szenario-Anhang</p>
                                  <p className="text-[11px] text-blue-700 leading-relaxed">{Object.keys(entwurf.szenarien).length} {Object.keys(entwurf.szenarien).length === 1 ? 'Szenario' : 'Szenarien'} durchgeführt</p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleSzenarioPdfDownload}
                                  className="shrink-0 h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                  data-testid={`cta-szenario-pdf-${entwurf.entwurf_id}`}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  PDF
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : entwurf.stufe_c_status === 'zahlung_laeuft' ? (
                          /* ZAHLUNG LÄUFT – prominente Recovery-UI */
                          <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50/60 space-y-3" data-testid={`pdf-zahlung-${entwurf.entwurf_id}`}>
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-amber-100 shrink-0">
                                <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-foreground">{t('dashboard.vollstaendigeVorlage')}</h4>
                                <p className="text-xs text-amber-700 leading-relaxed mt-1">
                                  {t('dashboard.stufeCZahlungLaeuft')}
                                </p>
                                <p className="text-xs text-amber-600 mt-1.5">
                                  {t('dashboard.stufeCZahlungHinweis')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleStufeCFreischalten}
                                disabled={checkoutLoading}
                                className="flex-1 h-9 text-xs font-semibold"
                                data-testid={`cta-stufec-retry-${entwurf.entwurf_id}`}
                              >
                                {checkoutLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CreditCard className="h-3.5 w-3.5 mr-1.5" />}
                                {t('dashboard.stufeCErneut')}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  fallanalyseAPI.stufeCCancel(entwurf.entwurf_id).then(() => {
                                    toast.info(t('dashboard.stufeCAbgebrochen'));
                                    onRefresh?.();
                                  }).catch(() => {});
                                }}
                                className="h-9 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                                data-testid={`cta-stufec-cancel-${entwurf.entwurf_id}`}
                              >
                                {t('dashboard.stufeCAbbrechen')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* GESPERRT: Prominente Bezahlschranke mit Mehrwert */
                          <div className="space-y-2">
                            <div className="p-4 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/[0.03] to-primary/[0.08] relative overflow-hidden" data-testid={`pdf-gate-${entwurf.entwurf_id}`}>
                              {/* Preis-Badge oben rechts */}
                              <div className="absolute top-0 right-0 bg-primary text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl" data-testid="price-badge">
                                49 €
                              </div>

                              <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                                  <Lock className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0 pr-14">
                                  <h4 className="text-base font-semibold text-foreground">{t('dashboard.vollstaendigeVorlage')}</h4>
                                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                    {t('dashboard.vollstaendigeVorlageHinweis')}
                                  </p>
                                </div>
                              </div>

                              {/* Mehrwert-Übersicht */}
                              <div className="bg-white/70 rounded-lg p-3 mb-4 border border-primary/10">
                                <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">{t('dashboard.freischaltUebersicht')}</p>
                                <div className="space-y-1.5">
                                  {[t('dashboard.freischaltF1'), t('dashboard.freischaltF2'), t('dashboard.freischaltF3'), t('dashboard.freischaltF4')].map((f, i) => (
                                    <div key={i} className="flex items-start gap-2 text-sm text-foreground">
                                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                      <span>{f}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* CTA Buttons */}
                              <div className="flex gap-2">
                                {istTestBerechtigt && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleTestUnlock}
                                    disabled={testLoading}
                                    className="h-10 text-xs px-3 border-amber-400 text-amber-700 hover:bg-amber-50"
                                    data-testid={`cta-test-unlock-${entwurf.entwurf_id}`}
                                  >
                                    {testLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3 mr-1" />}
                                    {t('dashboard.testmodusFreischalten')}
                                  </Button>
                                )}
                                <Button
                                  onClick={handleStufeCFreischalten}
                                  disabled={checkoutLoading}
                                  className="flex-1 h-10 text-sm font-semibold"
                                  data-testid={`cta-stufec-checkout-${entwurf.entwurf_id}`}
                                >
                                  {checkoutLoading ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('dashboard.stufeCWirdGestartet')}</>
                                  ) : (
                                    <><CreditCard className="h-4 w-4 mr-2" />{t('dashboard.stufeCFreischalten')}</>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* REGELBASIERT: Kein Checkout möglich */
                      <div className="flex items-center gap-2 p-3 rounded-lg border border-warm-200 bg-warm-50/30" data-testid={`pdf-regelbasiert-${entwurf.entwurf_id}`}>
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{t('dashboard.vollstaendigeVorlage')}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                            {t('dashboard.stufeCRegelbasiert')}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleLagebeurteilung}
                          className="shrink-0 h-8 text-xs"
                          data-testid={`cta-retry-lb-${entwurf.entwurf_id}`}
                        >
                          <RefreshCw className="h-3 w-3 mr-1.5" />
                          {t('dashboard.stufeCErneut')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ============================================
// OFFENE AUSSCHREIBUNGEN (für Dienstleister)
// ============================================
function OffeneAusschreibungenSection({ t, user }) {
  const [ausschreibungen, setAusschreibungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bewerbenId, setBewerbenId] = useState(null);
  const [nachricht, setNachricht] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const laden = async () => {
      try {
        const res = await sammelfallAPI.offeneAusschreibungen();
        setAusschreibungen(res.data.ausschreibungen || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    laden();
  }, []);

  const handleBewerben = async (ausschreibungId) => {
    if (nachricht.trim().length < 10) return;
    setSending(true);
    try {
      await sammelfallAPI.bewerben({
        ausschreibung_id: ausschreibungId,
        dienstleister_name: user?.name || 'Unbekannt',
        dienstleister_email: user?.email || '',
        nachricht: nachricht.trim(),
        user_id: user?.id,
      });
      toast.success(t('dlAusschreibung.bewerbungErfolg'));
      setBewerbenId(null);
      setNachricht('');
      // Reload
      const res = await sammelfallAPI.offeneAusschreibungen();
      setAusschreibungen(res.data.ausschreibungen || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('dlAusschreibung.bewerbungFehler'));
    } finally { setSending(false); }
  };

  if (loading) return null;
  if (ausschreibungen.length === 0) return null;

  return (
    <div className="mt-10 rounded-xl border-2 border-amber-300 bg-gradient-to-b from-amber-50 to-white p-6 shadow-sm" data-testid="offene-ausschreibungen-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2.5 rounded-lg bg-amber-600 shrink-0">
          <Megaphone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="font-serif text-xl font-bold text-amber-900">{t('dlAusschreibung.titel')}</h2>
          <p className="text-sm text-amber-700">{t('dlAusschreibung.untertitel')}</p>
        </div>
      </div>

      <div className="space-y-3">
        {ausschreibungen.map((a) => (
          <Card key={a.ausschreibung_id} className="border-amber-200 bg-white" data-testid={`ausschreibung-${a.ausschreibung_id}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">{a.titel}</h3>
                  {a.beschreibung && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.beschreibung}</p>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700 bg-amber-50">
                      {a.anzahl_faelle} {t('sammelfall.faelle')}
                    </Badge>
                    {a.region && (
                      <Badge variant="outline" className="text-[10px] border-warm-200 text-foreground/70">
                        {a.region}
                      </Badge>
                    )}
                    {a.frist && (
                      <Badge variant="outline" className="text-[10px] border-warm-200 text-foreground/70 gap-1">
                        <Clock className="h-2.5 w-2.5" /> {a.frist}
                      </Badge>
                    )}
                  </div>
                </div>
                <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200 shrink-0">
                  {a.bewerbungen_count || 0} {t('dlAusschreibung.bewerbungen')}
                </Badge>
              </div>

              {bewerbenId === a.ausschreibung_id ? (
                <div className="mt-3 pt-3 border-t border-warm-200 space-y-2" data-testid="bewerbung-formular">
                  <textarea
                    value={nachricht}
                    onChange={(e) => setNachricht(e.target.value)}
                    placeholder={t('dlAusschreibung.nachrichtPlaceholder')}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-warm-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none min-h-[80px] resize-none"
                    data-testid="bewerbung-nachricht"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleBewerben(a.ausschreibung_id)}
                      disabled={sending || nachricht.trim().length < 10}
                      className="gap-1.5"
                      data-testid="bewerbung-absenden-btn"
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      {t('dlAusschreibung.bewerbenBtn')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setBewerbenId(null); setNachricht(''); }}>
                      {t('sammelfall.abbrechen')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 border-primary/30 text-primary hover:bg-primary/5 gap-1.5"
                  onClick={() => setBewerbenId(a.ausschreibung_id)}
                  data-testid={`bewerben-btn-${a.ausschreibung_id}`}
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  {t('dlAusschreibung.bewerbenBtn')}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================
// MEINE AUSSCHREIBUNGEN + BEWERBUNGEN (für FBG)
// ============================================
function MeineAusschreibungenSection({ t, userId }) {
  const [ausschreibungen, setAusschreibungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [bewerbungen, setBewerbungen] = useState({});

  useEffect(() => {
    const laden = async () => {
      try {
        const res = await sammelfallAPI.meineAusschreibungen(userId);
        setAusschreibungen(res.data.ausschreibungen || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    if (userId) laden();
  }, [userId]);

  const ladeBewerbungen = async (ausschreibungId) => {
    if (bewerbungen[ausschreibungId]) {
      setExpandedId(expandedId === ausschreibungId ? null : ausschreibungId);
      return;
    }
    try {
      const res = await sammelfallAPI.ausschreibungAbrufen(ausschreibungId);
      setBewerbungen(prev => ({ ...prev, [ausschreibungId]: res.data.bewerbungen || [] }));
      setExpandedId(ausschreibungId);
    } catch { /* ignore */ }
  };

  if (loading || ausschreibungen.length === 0) return null;

  return (
    <div className="mt-6 rounded-xl border-2 border-emerald-300 bg-gradient-to-b from-emerald-50 to-white p-6 shadow-sm" data-testid="meine-ausschreibungen-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2.5 rounded-lg bg-emerald-600 shrink-0">
          <Megaphone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="font-serif text-xl font-bold text-emerald-900">{t('fbgAusschreibung.titel')}</h2>
          <p className="text-sm text-emerald-700">{t('fbgAusschreibung.untertitel')}</p>
        </div>
      </div>

      <div className="space-y-3">
        {ausschreibungen.map((a) => (
          <Card key={a.ausschreibung_id} className="border-emerald-200 bg-white" data-testid={`meine-ausschreibung-${a.ausschreibung_id}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">{a.titel}</h3>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">
                      {a.status === 'offen' ? t('fbgAusschreibung.statusOffen') : a.status}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-warm-200 text-foreground/70 gap-1">
                      <MessageCircle className="h-2.5 w-2.5" />
                      {a.bewerbungen_count || 0} {t('dlAusschreibung.bewerbungen')}
                    </Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => ladeBewerbungen(a.ausschreibung_id)}
                  className="shrink-0"
                  data-testid={`bewerbungen-toggle-${a.ausschreibung_id}`}
                >
                  {expandedId === a.ausschreibung_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>

              {expandedId === a.ausschreibung_id && (
                <div className="mt-3 pt-3 border-t border-warm-200 space-y-2" data-testid="bewerbungen-liste">
                  {(bewerbungen[a.ausschreibung_id] || []).length > 0 ? (
                    (bewerbungen[a.ausschreibung_id] || []).map((b) => (
                      <div key={b.bewerbung_id} className="p-3 rounded-lg bg-warm-50 border border-warm-200" data-testid={`bewerbung-${b.bewerbung_id}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold text-foreground">{b.dienstleister_name}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(b.erstellt).toLocaleDateString('de-DE')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{b.nachricht}</p>
                        {b.dienstleister_email && (
                          <a
                            href={`mailto:${b.dienstleister_email}`}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors"
                            data-testid={`kontakt-${b.bewerbung_id}`}
                          >
                            <Mail className="h-3 w-3" />
                            {b.dienstleister_email}
                          </a>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2">{t('fbgAusschreibung.keineBewerbungen')}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================
// DASHBOARD-SEITE
// ============================================
const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entwuerfe, setEntwuerfe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [istTestBerechtigt, setIstTestBerechtigt] = useState(false);
  const [sammelfaelle, setSammelfaelle] = useState([]);
  const paymentPollRef = useRef(null);

  const fetchEntwuerfe = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fallanalyseAPI.meineEntwuerfe(user.id);
      setEntwuerfe(res.data.entwuerfe || []);
    } catch (error) {
      console.error('Entwürfe laden fehlgeschlagen:', error);
      if (error?.response?.status === 401) {
        toast.error(t('dashboard.toastSitzungAbgelaufen'));
      } else {
        toast.error(t('dashboard.toastLadeFehler'));
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  const fetchSammelfaelle = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await sammelfallAPI.meine(user.id);
      setSammelfaelle(res.data.sammelfaelle || []);
    } catch { /* ignore */ }
  }, [user?.id]);

  // Test-Unlock-Berechtigung prüfen
  useEffect(() => {
    const checkTestStatus = async () => {
      try {
        const res = await fallanalyseAPI.testUnlockStatus();
        setIstTestBerechtigt(res.data.berechtigt === true);
      } catch {
        setIstTestBerechtigt(false);
      }
    };
    if (user?.id) checkTestStatus();
  }, [user?.id]);

  useEffect(() => {
    fetchEntwuerfe();
    fetchSammelfaelle();
  }, [fetchEntwuerfe, fetchSammelfaelle]);

  // Payment-Erfolg: Nach Stripe-Redirect den Status pollen
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      toast.info(t('dashboard.stufeCZahlungWirdGeprueft'));

      let attempts = 0;
      const maxAttempts = 20;

      const pollPayment = async () => {
        try {
          const res = await fallanalyseAPI.stufeCStatus(sessionId);
          if (res.data.freigeschaltet) {
            clearInterval(paymentPollRef.current);
            toast.success(t('dashboard.stufeCErfolg'));
            setSearchParams({});
            fetchEntwuerfe();
            return;
          }
          if (res.data.status === 'expired') {
            clearInterval(paymentPollRef.current);
            toast.error(t('dashboard.stufeCAbgelaufen'));
            setSearchParams({});
            fetchEntwuerfe();
            return;
          }
        } catch { /* ignore */ }

        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(paymentPollRef.current);
          toast.info(t('dashboard.stufeCTimeout'));
          setSearchParams({});
          fetchEntwuerfe();
        }
      };

      pollPayment();
      paymentPollRef.current = setInterval(pollPayment, 3000);

      return () => clearInterval(paymentPollRef.current);
    }

    if (paymentStatus === 'cancelled') {
      const cancelledEntwurfId = searchParams.get('entwurf_id');
      if (cancelledEntwurfId) {
        fallanalyseAPI.stufeCCancel(cancelledEntwurfId).catch(() => {});
      }
      toast.info(t('dashboard.stufeCAbgebrochen'));
      setSearchParams({});
      fetchEntwuerfe();
    }
  }, [searchParams, setSearchParams, fetchEntwuerfe, t]);

  const handlePremiumInteresse = async (entwurf) => {
    const falltyp = entwurf.hauptthema?.label || entwurf.erkannte_daten?.Maßnahme || 'unbekannt';
    try {
      await fallanalyseAPI.premiumInteresse(entwurf.entwurf_id, falltyp, 'dashboard');
      trackPremiumInteresseRegistriert(falltyp, 'dashboard');
      toast.success(t('dashboard.toastInteresse'));
      setEntwuerfe(prev => prev.map(e =>
        e.entwurf_id === entwurf.entwurf_id
          ? { ...e, premium_interesse: true }
          : e
      ));
    } catch {
      toast.error(t('dashboard.toastInteresseFehler'));
    }
  };

  const handleNeuerCheck = () => {
    trackDashboardNeuerCheck();
    navigate('/schnell-check');
  };

  const handleNeuerSammelfall = async () => {
    try {
      const res = await sammelfallAPI.erstellen(null, 'fbg', user?.id);
      navigate(`/sammelfall/${res.data.sammelfall_id}`);
    } catch {
      toast.error(t('dashboard.sammelfallFehler'));
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-primary">Laden...</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-3xl" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">{t('dashboard.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {entwuerfe.length > 0
                ? `${entwuerfe.length} ${entwuerfe.length === 1 ? t('dashboard.countSingle') : t('dashboard.countPlural')}`
                : t('dashboard.empty')}
            </p>
          </div>
          <Button onClick={handleNeuerCheck} className="h-10" data-testid="cta-neuer-schnellcheck">
            <Plus className="h-4 w-4 mr-2" />{t('dashboard.neuerCheck')}
          </Button>
        </div>

        {/* Entwürfe */}
        {entwuerfe.length > 0 ? (
          <div className="space-y-3" data-testid="entwuerfe-liste">
            {entwuerfe.map((e) => (
              <EntwurfCard key={e.entwurf_id} entwurf={e} onPremiumInteresse={handlePremiumInteresse} onRefresh={fetchEntwuerfe} t={t} istTestBerechtigt={istTestBerechtigt} sprache={language} />
            ))}
          </div>
        ) : (
          <Card className="border-warm-200 border-dashed" data-testid="keine-entwuerfe">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-warm-300 mx-auto mb-4" />
              <h2 className="font-serif text-lg font-semibold text-foreground mb-2">{t('dashboard.emptyTitle')}</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                {t('dashboard.emptyDescription')}
              </p>
              <Button onClick={handleNeuerCheck} className="h-11 px-6" data-testid="cta-erster-check">
                <Compass className="h-4 w-4 mr-2" />{t('dashboard.emptyButton')}<ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ====================================================================
            SAMMELFÄLLE FBG/WBV – UNMISSVERSTÄNDLICH HERVORGEHOBENE SEKTION
            ==================================================================== */}
        {(sammelfaelle.length > 0 || entwuerfe.some(e => e.zielgruppe === 'fbg' || e.zielgruppe === 'FBG/WBV')) && (
          <div className="mt-10 rounded-xl border-2 border-blue-300 bg-gradient-to-b from-blue-50 to-white p-6 shadow-sm" data-testid="sammelfaelle-section">
            {/* Header-Banner */}
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 rounded-lg bg-blue-600 shrink-0">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-xl font-bold text-blue-900" data-testid="sammelfaelle-title">
                  {t('dashboard.sammelfaelleTitle')}
                </h2>
                <p className="text-sm text-blue-700">{t('dashboard.sammelfaelleSubtitle')}</p>
              </div>
              <Badge className="bg-blue-600 text-white text-sm px-3 py-1 shrink-0" data-testid="sammelfaelle-preis">
                49 EUR
              </Badge>
            </div>

            <p className="text-sm text-blue-800 leading-relaxed mb-4 ml-[52px]">
              {t('dashboard.sammelfaelleDesc')}
            </p>

            {sammelfaelle.length > 0 ? (
              <div className="space-y-2 mb-4">
                {sammelfaelle.map((sf) => (
                  <Card
                    key={sf.sammelfall_id}
                    className="border-blue-200 hover:border-blue-400 transition-colors cursor-pointer bg-white"
                    onClick={() => navigate(`/sammelfall/${sf.sammelfall_id}`)}
                    data-testid={`sammelfall-card-${sf.sammelfall_id}`}
                  >
                    <CardContent className="py-3.5 px-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 rounded-lg bg-blue-100 shrink-0">
                          <FolderOpen className="h-4.5 w-4.5 text-blue-700" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">
                            {t('dashboard.sammelfall')} #{sf.sammelfall_id.slice(0, 8)}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700 bg-blue-50">
                              {sf.anzahl_faelle}/3 {t('dashboard.fallPlural')}
                            </Badge>
                            {sf.buendelung?.gesamt_potenzial === 'hoch' && (
                              <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">
                                {t('dashboard.buendelbar')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-blue-400 shrink-0" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-blue-200 border-dashed bg-blue-50/50 mb-4" data-testid="keine-sammelfaelle">
                <CardContent className="py-5 text-center">
                  <FolderOpen className="h-8 w-8 text-blue-300 mx-auto mb-2" />
                  <p className="text-sm text-blue-800 font-medium">{t('dashboard.sammelfaelleEmpty')}</p>
                  <p className="text-xs text-blue-600 mt-1">{t('dashboard.sammelfaelleEmptyHint')}</p>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={handleNeuerSammelfall}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-sm font-semibold"
              data-testid="cta-neuer-sammelfall"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('dashboard.neuerSammelfall')}
            </Button>

            {/* Meine Ausschreibungen (FBG) */}
            <MeineAusschreibungenSection t={t} userId={user?.id} />
          </div>
        )}

        {/* Offene Ausschreibungen (für Dienstleister) */}
        {user?.role === 'anbieter' && (
          <OffeneAusschreibungenSection t={t} user={user} />
        )}
      </div>
    </AppShell>
  );
};

export default DashboardPage;
